<template>
  <main
    class="container report-tabs"
    :class="{ 'report-tabs-diff': diff }"
  >
    <b-modal
      id="change-column-order"
      size="lg"
      centered
      no-header
      no-footer
      no-close-on-backdrop
      no-close-on-esc
    >
      <template #default="{hide}">
        <template v-if="orderedColumns">
          <h3>
            Change Order of Columns
            in <em v-text="groupForColumnsOrdering.title" /> Group
          </h3>
          <draggable
            v-model="orderedColumns"
            item-key="name"
            class="overflow-auto p-2 m-2"
            style="max-height: 500px"
          >
            <template #item="{ element: column }">
              <div
                :class="{'text-muted': savingColumnOrder, 'border': true, 'my-1': true, 'p-1': true, draggable: true}"
                :data-test="`column-reorderer-${column.name}`"
                @dragstart="dragStart"
                @dragend="dragEnd"
              >
                <strong>
                  <i class="bi bi-grip-vertical" /> {{ column.title }}
                </strong>
              </div>
            </template>
          </draggable>
          <div
            v-if="orderColumnError"
            class="text-danger ms-2 my-2"
          >
            {{ orderColumnError }}
          </div>
          <b-button
            class="ms-1"
            variant="primary"
            :disabled="savingColumnOrder"
            data-test="save-column-order"
            @click="saveColumnOrder()"
          >
            <template v-if="savingColumnOrder">
              Saving... <b-spinner small />
            </template>
            <template v-else>
              Save
            </template>
          </b-button>
          <b-button
            :disabled="savingColumnOrder"
            class="ms-1"
            variant="secondary"
            @click="hide()"
          >
            Cancel
          </b-button>
        </template>
      </template>
    </b-modal>
    <b-modal
      id="column-order-successfuly-saved-message"
      size="lg"
      centered
      no-header
      no-footer
      no-close-on-backdrop
      no-close-on-esc
    >
      <h3 v-if="groupForColumnsOrdering">
        Order of Columns in {{ groupForColumnsOrdering.title }} Group
        Successfuly Saved!
      </h3>
      <b-button
        :disabled="savingColumnOrder"
        class="ms-1 float-end"
        variant="primary"
        @click="reload()"
      >
        OK
      </b-button>
    </b-modal>
    <div
      v-if="isLoading"
      class="spinner-border ms-5"
      role="status"
    >
      <span class="visually-hidden">Loading...</span>
    </div>
    <b-alert
      v-else-if="error"
      show
      variant="danger"
    >
      <template v-if="error === 'Access denied.'">
        This report is unavailable at the moment. In order to access the data,
        please revisit site that contained link to this report or
        <a href="mailto:blake@profound.net">contact us</a>.
      </template>
      <template v-else>
        Unexpected server error occurred.
      </template>
    </b-alert>
    <b-tabs
      v-else-if="showData"
      ref="tabs"
      v-model:index="tab"
      content-class="p-3 bg-light border-start border-end border-bottom rounded-bottom"
      active-nav-item-class="bg-light border-start border-end border-top"
    >
      <b-tab active>
        <template #title>
          <span data-test="groups-tab-title">Groups</span>
        </template>
        <div
          v-if="diff"
          class="mb-2 small"
          data-test="diff-total-rows"
        >
          <b-badge variant="secondary" class="me-1">A</b-badge>
          {{ data.filename }} &middot; {{ data.totalRows }} rows
          <span class="mx-2">&harr;</span>
          <b-badge variant="secondary" class="me-1">B</b-badge>
          {{ filenameB }} &middot; {{ totalRowsB.toLocaleString() }} rows
        </div>
        <b-form-radio-group
          v-if="diff"
          v-model="diffMetric"
          class="mb-2"
          size="sm"
          buttons
          button-variant="outline-primary"
          data-test="diff-metric-toggle"
        >
          <b-form-radio value="count" data-test="diff-metric-count">
            Absolute Counts
          </b-form-radio>
          <b-form-radio value="coverage" data-test="diff-metric-coverage">
            Relative Coverage
          </b-form-radio>
          <b-form-radio value="uniques" data-test="diff-metric-uniques">
            Uniques
          </b-form-radio>
        </b-form-radio-group>
        <FileColumns
          ref="columns"
          :data="data"
          :diff="diff"
          :diff-metric="diffMetric"
          :total-rows-b="totalRowsB"
          @group-left-clicked="loadGroupData($event, true)"
          @group-middle-clicked="loadGroupData($event, false)"
          @column-clicked="columnClicked"
          @group-title-changed="groupTitleChanged($event)"
          @change-column-order="initializeChangeColumnOrder($event)"
        />
      </b-tab>
      <b-tab v-if="!diff">
        <template #title>
          <span data-test="file-overview-tab-title">File Overview</span>
        </template>
        <FileMetadata
          ref="metadata"
          :task-id="taskId"
          :data="data"
          @column-clicked="columnClicked"
          @column-shown="columnShown"
          @column-hidden="columnHidden"
        />
      </b-tab>
      <b-tab
        v-if="!diff && (deployAll || data.customReportMd)"
        title="Insights"
      >
        <CustomReport :init-report-code="data.customReportMd" />
      </b-tab>
      <b-tab
        v-if="!diff && deployAll"
        class="px-2 h-100 overflow-auto"
        title="Visitor Statistics"
        lazy
      >
        <VisitorStatistics />
      </b-tab>
      <b-tab
        v-for="group in visualizedGroups"
        :key="group.name"
      >
        <template #title>
          Data:
          <strong
            data-test="visualization-tab-title"
          >
            {{ group.title }}
          </strong>
          <div
            v-if="group.isLoading"
            class="spinner-border spinner-border-sm ms-2 mb-1"
            role="status"
          >
            <span class="visually-hidden">Loading...</span>
          </div>
          <i
            class="bi bi-x"
            @click="closeVisualization(group)"
          />
        </template>
        <FileColumnMostCommonValues
          v-if="group.data"
          :ref="`${group.name}MostCommon`"
          :group="group"
          :total-rows="totalRows()"
          @group-title-changed="groupTitleChanged(group)"
          @change-column-order="initializeChangeColumnOrder($event)"
        />
      </b-tab>
    </b-tabs>
    <section v-else>
      <b-form @submit.prevent="forceShowingReport">
        <b-alert
          show
          variant="warning"
        >
          <h1>Configuration consistency problems detected</h1>
          <!-- Was `<big>`; Vue 3 does not know that deprecated tag and warns
               "Failed to resolve component: big" on every Report render. -->
          <div class="fs-5">
            <p>
              Analysis results are inconsistent with applied configuration.
            </p>
            <template v-if="data.problems.multigroupColumns">
              <p>The following columns appear in multiple groups:</p>
              <p>
                <ul>
                  <li
                    v-for="([columnName, groups]) in data.problems.multigroupColumns"
                    :key="columnName"
                  >
                    {{ columnName }}
                    <ul>
                      <li
                        v-for="group in groups"
                        :key="group"
                      >
                        {{ group }}
                      </li>
                    </ul>
                  </li>
                </ul>
              </p>
            </template>
            <template v-if="data.problems.emptyGroups">
              <p>
                The following groups do not have any column found in the report:
              </p>
              <p>
                <ul>
                  <li
                    v-for="group in data.problems.emptyGroups"
                    :key="group.name"
                  >
                    {{ group.name }}
                  </li>
                </ul>
              </p>
            </template>
            <template v-if="data.problems.undefinedGroups">
              <p>
                The following group names appear in groups ordering,
                but aren't defined in grouping setup:
              </p>
              <p>
                <ul>
                  <li
                    v-for="groupName in data.problems.undefinedGroups"
                    :key="groupName"
                  >
                    {{ groupName }}
                  </li>
                </ul>
              </p>
            </template>
          </div>
        </b-alert>
        <b-form-group>
          <b-form-checkbox-group>
            <b-form-checkbox v-model="dontShowAgain">
              Don't show this message again
            </b-form-checkbox>
          </b-form-checkbox-group>
        </b-form-group>
        <b-button
          type="submit"
          variant="primary"
        >
          Show the report anyway
        </b-button>
      </b-form>
    </section>
  </main>
</template>

<script>
import draggable from "vuedraggable";
import FileMetadata from "./FileMetadata.vue";
import FileColumns from "./FileColumns.vue";
import FileColumnMostCommonValues from "./FileColumnMostCommonValues.vue";
import httpHelper from "../../modules/httpHelper.js";
import { parseStat } from "../../modules/reportStats.js";
import { csvInsideUrl, fetchCsvInside } from "../../modules/csvInside.js";
import CustomReport from './CustomReport.vue';
import VisitorStatistics from "./VisitorStatistics.vue";

export default {
  components: {
    FileMetadata,
    FileColumns,
    FileColumnMostCommonValues,
    draggable,
    CustomReport,
    VisitorStatistics,
  },
  data() {
    return {
      tab: 0,
      data: null,
      visualizedGroups: [],
      isLoading: true,
      showData: false,
      dontShowAgain: false,
      draggingClasses: ["dragging", "bg-secondary", "text-light"],
      groupForColumnsOrdering: null,
      orderedColumns: null,
      savingColumnOrder: false,
      orderColumnError: null,
      error: null,
      customReportMd: null,
      diffMetric: ["uniques", "coverage"].includes(this.$route.query.metric)
        ? this.$route.query.metric
        : "count",
      totalRowsB: 0,
      filenameB: "",
    };
  },
  computed: {
    deployAll: () =>
      ["true", "t", "yes", "y", "on"].includes(
        process.env.VUE_APP_DEPLOY_ALL.toLowerCase()
      ),
    backendUrl: () => process.env.VUE_APP_VISUALIZER_BACKEND_URL,
    backendToken: () => process.env.VUE_APP_BACKEND_TOKEN,
    // The diff route (`/diff/:taskId1/:taskId2`) reuses this component: the
    // primary report is taskId1, the second is merged in as B + delta columns.
    diff() {
      return this.$route.name === "diff";
    },
    taskId() {
      return this.$route.params.taskId || this.$route.params.taskId1;
    },
    taskId2() {
      return this.$route.params.taskId2;
    },
  },
  async mounted() {
    await this.fetchData(csvInsideUrl(this.taskId));
    if (this.data && this.diff) {
      await this.loadSecondReport();
    }
    if (this.data) {
      this.showData =
        this.diff ||
        !this.deployAll ||
        this.data.ignoreInconsistency ||
        !this.data.problems;
    }
    this.isLoading = false;
  },
  methods: {
    reload: () => location.reload(),
    toNumber: parseStat,
    // Coverage deltas are displayed with one decimal, so round them at the
    // source: a 0.04 pp change must not colour the column name or count as a
    // divergence while the cell reads "0 pp".
    roundCoverageDelta(delta) {
      return delta == null ? null : Math.round(delta * 10) / 10;
    },
    // B minus A, or null when either side has no value for the statistic.
    statDelta(a, b) {
      const valueA = this.toNumber(a);
      const valueB = this.toNumber(b);
      if (valueA == null || valueB == null) {
        return null;
      }
      return valueB - valueA;
    },
    // In diff mode, fetch the second report and graft its per-column count /
    // uniques + the A→B delta onto the primary report's columns.
    async loadSecondReport() {
      // Fetch the report we're diffing against (taskId2).
      const { response, data } = await fetchCsvInside(this.taskId2);

      // Surface an error and bail if the second report can't be loaded.
      if (!response.ok || !data || !Array.isArray(data.groups)) {
        this.error =
          (data && Array.isArray(data.errors) && data.errors[0]) || "unknown";
        this.data = null;
        return;
      }

      // Columns are matched by name (order-independent), so reports whose
      // headers differ or are reordered still diff fine — the union of both
      // reports' columns is shown, with columns missing on either side flagged.

      // Index the second report's columns by name for O(1) lookup.
      const columnsB = {};
      data.groups.forEach((group) => {
        group.columns.forEach((column) => {
          columnsB[column.name] = column;
        });
      });

      // Graft each B column's stats and the A→B delta onto the primary report.
      // A column absent from B keeps an empty B side and is flagged onlyInA.
      this.totalRowsB = this.toNumber(data.totalRows) || 0;
      this.filenameB = data.filename;
      const emptyColumn = { numFills: "0", numUniques: 0, fillRate: 0 };
      const namesInA = new Set();
      this.data.groups.forEach((group) => {
        group.columns.forEach((column) => {
          namesInA.add(column.name);
          const b = columnsB[column.name];
          column.b = b || emptyColumn;
          column.onlyInA = !b;
          column.countDelta = this.statDelta(column.numFills, column.b.numFills);
          column.uniquesDelta = this.statDelta(
            column.numUniques,
            column.b.numUniques
          );
          // Relative coverage delta (fillRate B - fillRate A), in percentage
          // points. Used to surface columns whose absolute count rises while
          // their coverage of the (larger) file actually falls (note 34360).
          column.coverageDelta = this.roundCoverageDelta(
            this.statDelta(column.fillRate, column.b.fillRate)
          );
        });
      });

      // Append columns present only in B so the listing shows the full union.
      // Their A side is empty and they are flagged onlyInB. B-only columns are
      // slotted into the same-named group, or a new group if none matches.
      const groupsByName = {};
      this.data.groups.forEach((group) => {
        groupsByName[group.name] = group;
      });
      data.groups.forEach((groupB) => {
        groupB.columns.forEach((columnB) => {
          if (namesInA.has(columnB.name)) {
            return;
          }
          let group = groupsByName[groupB.name];
          if (!group) {
            group = {
              name: groupB.name,
              title: groupB.title,
              columns: [],
              data: null,
              isLoading: false,
            };
            groupsByName[groupB.name] = group;
            this.data.groups.push(group);
          }
          group.columns.push({
            ...columnB,
            numFills: "0",
            fillRate: 0,
            numUniques: "0",
            numZero: null,
            b: columnB,
            onlyInB: true,
            countDelta: this.statDelta("0", columnB.numFills),
            uniquesDelta: this.statDelta("0", columnB.numUniques),
            coverageDelta: this.roundCoverageDelta(
              this.statDelta(0, columnB.fillRate)
            ),
          });
        });
      });

      // The single report hides the columns listed in `columnsToHide` (the
      // File Overview tab emits `columnHidden` for each of them on mount);
      // that tab is not rendered in diff mode, so apply the lists here. A
      // column hidden in either report has no statistics on that side, so it
      // is hidden for the whole diff.
      const hidden = new Set([
        ...(this.data.columnsToHide || []),
        ...(data.columnsToHide || []),
      ]);
      this.data.groups.forEach((group) => {
        group.columns.forEach((column) => {
          column.hiddenInDiff = hidden.has(column.name);
        });
      });

      // Show both filenames in the navbar, tagged (A)/(B) to match the
      // `Count (A)` / `Count (B)` column headers in the table below.
      window.app.$refs.navbar.filename =
        `${this.data.filename} (A) ↔ ${data.filename} (B)`;
    },
    async fetchData(url) {
      const response = await fetch(url);
      const data = await httpHelper.safeJson(response);
      if (response.ok && data && Array.isArray(data.groups)) {
        window.app.$refs.navbar.filename = data.filename;
        window.app.$refs.navbar.moodysBranded = Boolean(data.moodysBranded);
        data.groups = data.groups.map((element) => {
          element.data = null;
          element.isLoading = false;
          return element;
        });
        this.data = data;
        return;
      }
      if (data && Array.isArray(data.errors) && data.errors.length > 0) {
        this.error = data.errors[0];
        return;
      }
      this.error = "unknown";
    },
    totalRows() {
      if (this.data == null || this.data.totalRows == null) return 0;
      return parseInt(String(this.data.totalRows).replaceAll(",", ""));
    },
    dragStart(event) {
      event.target.classList.add(...this.draggingClasses);
    },
    dragEnd(event) {
      event.target.classList.remove(...this.draggingClasses);
    },
    initializeChangeColumnOrder(group) {
      this.groupForColumnsOrdering = { name: group.name, title: group.title };
      this.orderedColumns = [...group.columns];
      this.$bvModal.show("change-column-order");
    },
    async saveColumnOrder() {
      this.savingColumnOrder = true;
      this.orderColumnError = null;
      const taskId = this.$route.params.taskId;
      let urlBase = `save-column-order/${taskId}`;
      const url = `${this.backendUrl}/${urlBase}/?token=${this.backendToken}`;
      const body = JSON.stringify({
        group: this.groupForColumnsOrdering.name,
        orderedColumns: this.orderedColumns.map((column) => column.name),
      });
      const response = await fetch(url, { method: "POST", body });
      this.savingColumnOrder = false;
      if (!response.ok) {
        const data = await httpHelper.safeJson(response);
        const firstError = data && data.errors && data.errors[0];
        if (firstError === "Couldn't determine provided group.") {
          this.orderColumnError = firstError;
          return;
        }
        this.orderColumnError = "Unknown error occurred.";
        return;
      }
      this.$bvModal.hide("change-column-order");
      this.$bvModal.show("column-order-successfuly-saved-message");
    },
    groupTitleChanged(group) {
      const visualizedGroup = this.visualizedGroups.find(
        (visualizedGroup) => visualizedGroup.name === group.name
      );
      if (visualizedGroup) {
        visualizedGroup.title = group.title;
      }
      this.data.groups.find(
        (dataGroup) => dataGroup.name === group.name
      ).title = group.title;
      this.$refs.metadata.$refs.searchBox.filter();
      this.$refs.columns.$refs.searchBox.filter();
    },
    openInNewTab(group) {
      // Tab order: 0=Groups, 1=File Overview, [2=Insights if deployAll||customReportMd],
      // [next=Visitor Statistics if deployAll], then visualizedGroups in order.
      let offset = 2;
      if (this.deployAll || (this.data && this.data.customReportMd)) {
        offset += 1;
      }
      if (this.deployAll) {
        offset += 1;
      }
      const idx = this.visualizedGroups.findIndex((g) => g.name === group.name);
      if (idx >= 0) {
        this.tab = offset + idx;
      }
    },
    initVisualization(group, openInNewTab) {
      if (!this.visualizedGroups.includes(group)) {
        this.visualizedGroups.push(group);
      }
      if (openInNewTab) {
        this.$nextTick(() => this.openInNewTab(group));
      }
    },
    columnEntry(group, columnName) {
      return group.columns.find((column) => column.name === columnName);
    },
    closeVisualization(groupToClose) {
      this.visualizedGroups = this.visualizedGroups.filter(
        (group) => group !== groupToClose
      );
    },
    async loadGroupData(group, openInNewTab) {
      if (this.diff) {
        return;
      }
      this.initVisualization(group, openInNewTab);
      if (group.data !== null) {
        return;
      }
      group.isLoading = true;
      const backendToken = process.env.VUE_APP_BACKEND_TOKEN;
      const taskId = this.$route.params.taskId;
      const body = JSON.stringify({ taskId, groupName: group.name });
      const url = `${this.backendUrl}/most-common/?token=${backendToken}`;
      const response = await fetch(url, { method: "POST", body });
      const groupData = await httpHelper.safeJson(response);
      group.data = groupData.columns;
      group.attachedImages = groupData.attachedImages;
      group.isLoading = false;
      group.data = Object.fromEntries(
        Object.entries(group.data).map(([columnName, column]) => {
          const isHidden = this.columnEntry(group, columnName).isHidden;
          const definition = this.data.collection.fields.find(
            field => columnName.match(`^${field.name}$`)
          ) || {description: ''};
          return [columnName, {...column, definition, isHidden}];
        })
      );
    },
    async columnClicked(clickType, column, group) {
      await this.loadGroupData(group, clickType === "left");
      const mostCommonElement = this.$refs[`${group.name}MostCommon`][0];
      const columnIndex = mostCommonElement
        .allColumns()
        .findIndex(([elementColumnName]) => elementColumnName === column.name);
      mostCommonElement.activeColumn = columnIndex;
    },
    isHistogramEntryFailure(entry) {
      return parseInt(entry.columnCount) !== this.data.columnCount;
    },
    async forceShowingReport() {
      this.showData = true;
      if (!this.dontShowAgain) {
        return;
      }
      const urlBase = `ignore-inconsistency/${this.taskId}`;
      const url = `${this.backendUrl}/${urlBase}/?token=${this.backendToken}`;
      await fetch(url, { method: "POST" });
    },
    columnShown(columnName) {
      this.$refs.columns.show(columnName);
    },
    columnHidden(columnName) {
      this.$refs.columns.hide(columnName);
    },
  },
};
</script>

<style scoped>
main {
  height: 100vh;
  display: flex;
  flex-flow: column;
}

.draggable {
  cursor: move;
}
.dragging {
  opacity: 0.5;
}
</style>

<style>
.report-tabs .nav-item > * {
  border-bottom: 0px;
}
/* non-scoped styles here due to using bootstrap-vue component styles overloading */
.report-tabs .tabs,
.report-tabs .tab-content {
  flex: 1;
  display: flex;
  flex-flow: column;
}
/* Only the ACTIVE tab-pane should stretch as a flex column. Applying the
 * flex/display rules to `.tab-pane` unconditionally overrides BS5's
 * `.tab-pane { display: none }` for inactive panes, so all tabs render
 * stacked (Groups content pushes File Overview / Insights below it). Gate
 * on `.active` — same specificity boost as BS5's own `.tab-pane.active`
 * rule. */
.report-tabs .tab-pane.active {
  flex: 1;
  display: flex;
  flex-flow: column;
}
.report-tabs .tab-pane {
  min-height: 600px;
  max-height: calc(100vh - 13rem);
}
/* The diff view has a single sticky metric header inside its scroller, so the
   page itself must never scroll: on short viewports the 600px minimum would
   push the scroller (and its header) under the fixed navbar. Let the pane
   shrink to the space left by 100vh instead. */
.report-tabs-diff .tab-pane {
  min-height: 0;
}
.report-tabs-diff .tab-content,
.report-tabs-diff .tab-pane,
.report-tabs-diff .groups-listing,
.report-tabs-diff .scroller-container {
  min-height: 0;
}
</style>
